import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Freeze} from 'react-freeze';
import {Platform, StyleSheet, View} from 'react-native';
import Animated from 'react-native-reanimated';
import {useIsFocused} from '@react-navigation/native';
import {ThemeProvider} from '@rneui/themed';
import {FlashList, FlashListRef, ListRenderItem} from '@shopify/flash-list';
import {observer} from 'mobx-react-lite';

import ItemSeparator from '../../components/ItemSeparator';
import {ProductTileCarousel} from '../../components/ProductTile';
import {isDepartmentJoinId, joinTitle} from '../../components/SiteHeader/menu/departmentJoins';
import {resolveJoin} from '../../components/SiteHeader/menu/resolveMenus';
import StickyFilterPillBar from '../../components/StickyFilterBar';
import ContentView from '../../components/ui-controls/layout/ContentView';
import {useSearchDispatch, useSearchState} from '../../context/SearchContext';
import {space} from '../../design-system/tokens';
import useStorePriceDisplayConditions from '../../hooks/usePriceDisplayConditions';
import {useScrollCollapseJSHandler} from '../../hooks/useScrollCollapse';
import {ScreenTypes, useWebScreenDimensions} from '../../hooks/useWebScreenDimensions';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {useDepartmentJoinLines} from '../../queries/departmentJoin/useDepartmentJoinLines';
import {useStores} from '../../stores/use-stores';
import rewardsTheme from '../../theme/rewardsTheme';
import {OrderLine} from '../../transport/apiServiceResponse';
import {logger, serializeError} from '../../utils/logger';
import {applyTierSort} from '../../utils/rewards';

import {RangeListEmpty, RangeListFooter, RangeListSkeleton} from './RangeProductListScreen';
import {useCollapsingListLayout} from './useCollapsingListLayout';

const NO_IDS: string[] = [];

/**
 * DIS-1899: product list over a named join of several MFL departments (`/shop/all/:join`).
 * DIS-1907: a search-only bar above the list. Still no sort control and no filter control.
 * The header compacts on scroll as in RangeProductListScreen, reversing ruling RC3.
 */
const JoinedDepartmentsScreen = observer(
  ({route}: RouteNavigatorScreenProps<AppRoute.SHOP_ALL>) => {
    const {rangeStore, shopSelectionStore} = useStores();
    const {isWebDesktopScreen, screenType} = useWebScreenDimensions();
    const listRef = useRef<FlashListRef<OrderLine>>(null);
    const {scrollHandler} = useScrollCollapseJSHandler(listRef);
    const {
      listWrapperStyle,
      onListWrapperLayout,
      wrapperStaticStyle,
      wrapperAnimatedStyle,
      spacerStyle,
      maxCollapse,
      resetScrollCollapse,
    } = useCollapsingListLayout();
    // DIS-1127 short-content fix: see `maxCollapse` in useCollapsingListLayout.
    const listContentStyle = useMemo(
      () => ({...styles.listContent, paddingBottom: maxCollapse}),
      [maxCollapse],
    );
    const join = route.params?.join;
    const specials = route.params?.specials;
    const storeId = shopSelectionStore.selectedStore?.id ?? null;

    // Departments belong to the store they were loaded for; never resolve against another's tree.
    const storeMatches = rangeStore.departmentsLoadStoreId === storeId;
    const loadState = storeMatches ? rangeStore.departmentsLoadState : 'idle';
    const departments = rangeStore.departments;
    const departmentIds = useMemo(
      () =>
        loadState === 'loaded' && join && isDepartmentJoinId(join)
          ? resolveJoin(departments, join).departments.map(d => d.id)
          : NO_IDS,
      [loadState, departments, join],
    );

    // DIS-1907: SearchContext is shared by the tab screens, so its query may come from another
    // list. Clear it on mount and on a join or specials change, and ignore it until the clear
    // has landed so no request goes out with the other list's keywords.
    const searchState = useSearchState();
    const searchDispatch = useSearchDispatch();
    const scope = `${join}|${!!specials}`;
    const [ownedScope, setOwnedScope] = useState<string | null>(null);
    useEffect(() => {
      searchDispatch({type: 'SET_QUERY', query: ''});
      searchDispatch({type: 'SET_SEARCH_BAR_EXPANDED', expanded: false});
      setOwnedScope(scope);
    }, [scope, searchDispatch]);
    const liveKeywords = ownedScope === scope ? searchState.query : '';
    // DIS-2046: web keeps a covered screen mounted, and another list's search bar writes the same
    // SearchContext. Hold the last focused value so a hidden screen does not refetch per term.
    const isFocused = useIsFocused();
    const heldKeywords = useRef(liveKeywords);
    if (isFocused) {
      heldKeywords.current = liveKeywords;
    }
    const keywords = heldKeywords.current;

    // Scroll to top and uncollapse the header when the join, specials or keywords change, as
    // RangeProductListScreen does on a search change.
    const isFirstRender = useRef(true);
    useEffect(() => {
      if (isFirstRender.current) {
        isFirstRender.current = false;
        return;
      }
      listRef.current?.scrollToOffset({offset: 0, animated: false});
      resetScrollCollapse();
    }, [scope, keywords, resetScrollCollapse]);

    // DIS-2007: the private member_price set only for an entitled shopper, as MembersPriceListScreen.
    const {showMemberPrice} = useStorePriceDisplayConditions();
    // Called unconditionally; `[]` makes the hook idle while departments are not ready.
    const query = useDepartmentJoinLines({
      storeId,
      departmentIds,
      specials,
      keywords,
      includePrivate: showMemberPrice.privatePricing,
    });

    const {error} = query;
    useEffect(() => {
      if (error) {
        logger.warn(
          'JoinedDepartmentsScreen',
          'Department join partly failed',
          serializeError(error),
        );
      }
    }, [error]);

    // DIS-2007: a specials list loads every page before it renders, then tier-sorts once.
    // Sorting only the loaded pages would move tiles as each later page arrives.
    // DIS-2028: web keeps a covered stack screen mounted. Page only while focused; a paused
    // drain resumes on refocus.
    // DIS-2032: web hides a covered stack screen with display:none, so its list is 0 x 0, and
    // FlashList then renders every loaded row. Freeze stops the list rendering while it is hidden.
    // Web only: DIS-2032 measured the growth on web; native keeps its current behaviour.
    const freezeList = Platform.OS === 'web' && !isFocused;
    const {lines, hasNextPage, isFetchingNextPage, fetchNextPage} = query;
    useEffect(() => {
      if (isFocused && specials && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }, [isFocused, specials, hasNextPage, isFetchingNextPage, fetchNextPage]);
    const specialsDraining = !!specials && (hasNextPage || isFetchingNextPage);
    // `lines` is a new array on every render, so a memo would never hit. The list is hidden
    // while specials drain, so sort only once draining ends.
    const sortedLines = specials && !specialsDraining ? applyTierSort(lines) : lines;

    let numColumns = 2;
    switch (screenType) {
      case ScreenTypes.mobile:
        numColumns = 2;
        break;
      case ScreenTypes.desktop:
        numColumns = 4;
        break;
      case ScreenTypes.desktopLarge:
        numColumns = 6;
        break;
    }

    const renderItem = useCallback<ListRenderItem<OrderLine>>(
      ({item}) => (
        <View style={styles.tileWrapper}>
          <ProductTileCarousel item={item} fullWidth testContext="category" />
        </View>
      ),
      [],
    );

    const departmentsPending = loadState === 'idle';

    return (
      <ContentView testID="joined-departments-screen" style={styles.contentViewOverride}>
        <View style={listWrapperStyle} onLayout={onListWrapperLayout}>
          {/* Wrapper translateY follows header collapse, as in RangeProductListScreen: spacer, bar
              and list slide up as one unit. */}
          <Animated.View style={[wrapperStaticStyle, wrapperAnimatedStyle]}>
            {/* Reserves the area under the transparent route header. */}
            <View style={spacerStyle} pointerEvents="none" />
            {/* Outside the loading branch, so the bar stays while a new query loads. Keyed by
                scope: a join or specials change resets the bar's local input text. */}
            {join && isDepartmentJoinId(join) ? (
              <StickyFilterPillBar key={scope} searchOnly scopeName={joinTitle(join, specials)} />
            ) : null}
            {/* Outside the list, as RangeProductListScreen: in the FlashList empty slot a loading view is 0 x 0 on web. */}
            {departmentsPending || query.isLoading || specialsDraining ? (
              <RangeListSkeleton numColumns={numColumns} />
            ) : (
              <Freeze freeze={freezeList}>
                <ThemeProvider theme={rewardsTheme}>
                  <FlashList
                    // A join or specials change reuses this tab screen; remount so the list opens at the top.
                    key={`joined-departments-flashlist-${numColumns}-${join}-${!!specials}`}
                    testID="joined-departments-list"
                    data={sortedLines}
                    renderItem={renderItem}
                    numColumns={numColumns}
                    style={styles.list}
                    onScroll={scrollHandler}
                    scrollEventThrottle={16}
                    showsVerticalScrollIndicator={isWebDesktopScreen}
                    onEndReachedThreshold={isWebDesktopScreen ? 0.5 : 3}
                    onEndReached={() => {
                      // Specials drain every page in the effect above; only the plain join pages here.
                      if (isFocused && !specials && hasNextPage && !isFetchingNextPage) {
                        fetchNextPage();
                      }
                    }}
                    keyExtractor={item => item.id}
                    ItemSeparatorComponent={ItemSeparator}
                    ref={listRef}
                    ListEmptyComponent={<RangeListEmpty isFetching={false} />}
                    ListFooterComponent={
                      <RangeListFooter isFetchingNextPage={query.isFetchingNextPage} />
                    }
                    contentContainerStyle={listContentStyle}
                  />
                </ThemeProvider>
              </Freeze>
            )}
          </Animated.View>
        </View>
      </ContentView>
    );
  },
);

export default JoinedDepartmentsScreen;

const styles = StyleSheet.create({
  // SHORTCUT: zero removes ContentView's side padding so tiles run edge to edge, as in
  // RangeProductListScreen. No zero in `space`; ceiling: none. Upgrade: a `space.s0` token (DIS-1735).
  contentViewOverride: {paddingHorizontal: 0},
  tileWrapper: {flex: 1, paddingHorizontal: space.s4},
  list: {flex: 1},
  listContent: {paddingHorizontal: space.s4},
});
